// Copyright 2025, University of Maryland, All Rights Reserved
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bulkOperationsApi } from '../api/bulk-operations';
import { ArrowLeft, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

export default function BulkComparisonResultsPage() {
  const { comparisonId } = useParams<{ comparisonId: string }>();
  const navigate = useNavigate();

  const { data: comparison, isLoading, error } = useQuery({
    queryKey: ['bulk-comparison', comparisonId],
    queryFn: () => bulkOperationsApi.getBulkComparisonStatus(comparisonId as string),
    enabled: !!comparisonId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading comparison results...</div>
      </div>
    );
  }

  if (error || !comparison) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold">Error loading comparison</h3>
        <p className="text-red-600">{error ? (error as Error).message : 'Comparison not found'}</p>
      </div>
    );
  }

  if (comparison.status !== 'completed') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-yellow-800 font-semibold">Comparison in Progress</h3>
        <p className="text-yellow-600">This comparison is still being processed. Status: {comparison.status}</p>
      </div>
    );
  }

  const result = comparison.result;
  if (!result) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-gray-800 font-semibold">No Results Available</h3>
        <p className="text-gray-600">The comparison completed but no results were generated.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/bulk-comparison')}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Comparisons
          </button>
          <div className="text-sm text-gray-500">
            Completed in {comparison.processing_time_seconds?.toFixed(1)}s
            {comparison.cost && ` • Cost: $${comparison.cost.toFixed(4)}`}
          </div>
        </div>
        
        <h2 className="text-2xl font-bold mb-2">{result.comparison_title}</h2>
        <p className="text-gray-600">
          Compared {result.bucket_summaries.length} buckets • 
          {result.total_documents_analyzed} total documents analyzed
        </p>
      </div>

      {/* Bucket Summaries */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Bucket Summaries</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.bucket_summaries.map((bucket: any) => (
            <div key={bucket.bucket_id} className="border rounded-lg p-4">
              <h4 className="font-medium text-lg mb-2">{bucket.bucket_name}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Documents:</span>
                  <span className="font-medium">{bucket.document_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Avg Difficulty:</span>
                  <span className="font-medium">{bucket.average_difficulty.toFixed(1)}/10</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Difficulty Range:</span>
                  <span className="font-medium">
                    {bucket.difficulty_range.min.toFixed(1)} - {bucket.difficulty_range.max.toFixed(1)}
                  </span>
                </div>
                
                <div className="pt-2 border-t">
                  <p className="text-gray-600 mb-1">Common Concepts:</p>
                  <div className="flex flex-wrap gap-1">
                    {bucket.common_concepts.slice(0, 5).map((concept: string, idx: number) => (
                      <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {concept}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <p className="text-gray-600 mb-1">Key Strengths:</p>
                  <ul className="list-disc list-inside text-xs">
                    {bucket.key_strengths.slice(0, 2).map((strength: string, idx: number) => (
                      <li key={idx} className="text-green-700">{strength}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Findings */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Key Findings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1 text-orange-500" />
              Major Differences
            </h4>
            <ul className="space-y-1">
              {result.major_differences.map((diff: string, idx: number) => (
                <li key={idx} className="text-sm text-gray-600">• {diff}</li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-2 flex items-center">
              <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
              Major Similarities
            </h4>
            <ul className="space-y-1">
              {result.major_similarities.map((sim: string, idx: number) => (
                <li key={idx} className="text-sm text-gray-600">• {sim}</li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-2 flex items-center">
              <TrendingUp className="w-4 h-4 mr-1 text-blue-500" />
              Alignment Gaps
            </h4>
            <ul className="space-y-1">
              {result.alignment_gaps.map((gap: string, idx: number) => (
                <li key={idx} className="text-sm text-gray-600">• {gap}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Difficulty Comparison */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Difficulty Comparison</h3>
        <div className="space-y-3">
          {Object.entries(result.difficulty_comparison).map(([bucketName, difficulty]: [string, any]) => (
            <div key={bucketName} className="flex items-center">
              <span className="w-32 text-sm font-medium">{bucketName}</span>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div 
                  className="bg-blue-600 h-6 rounded-full flex items-center justify-end pr-2"
                  style={{ width: `${(difficulty / 10) * 100}%` }}
                >
                  <span className="text-xs text-white font-medium">{difficulty.toFixed(1)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
        
        <div className="mb-6">
          <h4 className="font-medium text-gray-700 mb-2">Overall Recommendations</h4>
          <ul className="space-y-2">
            {result.overall_recommendations.map((rec: string, idx: number) => (
              <li key={idx} className="flex items-start">
                <span className="text-blue-500 mr-2">→</span>
                <span className="text-sm text-gray-600">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-700 mb-2">Bucket-Specific Recommendations</h4>
          <div className="space-y-3">
            {Object.entries(result.recommendations_by_bucket).map(([bucketName, recs]: [string, any]) => (
              <div key={bucketName} className="border-l-4 border-blue-200 pl-4">
                <h5 className="font-medium text-sm mb-1">{bucketName}</h5>
                <ul className="space-y-1">
                  {recs.map((rec: string, idx: number) => (
                    <li key={idx} className="text-sm text-gray-600">• {rec}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quality Ranking */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Quality Ranking</h3>
        <div className="space-y-3">
          {result.quality_ranking.map((item: any, idx: number) => (
            <div key={idx} className="flex items-center p-3 border rounded-lg">
              <div className="text-2xl font-bold text-gray-400 mr-4">#{item.rank}</div>
              <div className="flex-1">
                <h4 className="font-medium">{item.bucket_name}</h4>
                <p className="text-sm text-gray-600">Score: {item.quality_score}/10</p>
                <p className="text-xs text-gray-500 mt-1">{item.justification}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Analysis Info */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          <strong>AI Analysis Complete:</strong> This comparison was analyzed using Claude Sonnet 4.
          {comparison.processing_time_seconds && 
            <span> Processing time: {comparison.processing_time_seconds.toFixed(1)}s</span>}
          {comparison.cost && 
            <span> • Cost: ${comparison.cost.toFixed(4)}</span>}
        </p>
      </div>
    </div>
  );
}
